import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { monolithApi } from '@/utils/api';
import {
  appBitriseYMLPath,
  buildBitriseYMLPath,
  deleteBuildBitriseYMLPath,
  deletePipelineBitriseYMLPath,
  hasBuildBitriseYmlPath,
  hasPipelineBitriseYmlPath,
  pipelineBitriseYMLPath,
  restoreAppBitriseYMLPath,
} from '@/utils/monolithApiRouteService';

// App-level yml hooks
type AppLevelProps = {
  appSlug: string;
  enabled?: boolean;
};
export type AppBitriseYml = {
  yml: string;
  updatedAt?: string;
};

export const useAppBitriseYml = ({ appSlug, enabled }: AppLevelProps) => {
  const url = appBitriseYMLPath(appSlug);
  return useQuery({
    queryKey: [url],
    queryFn: () => monolithApi.get<string>(url),
    select: ({ data, headers }): AppBitriseYml => {
      const updatedAt = headers['x-config-updated-at'];

      return { yml: data, updatedAt: typeof updatedAt === 'string' ? updatedAt : undefined };
    },
    enabled: !!appSlug && enabled,
  });
};

export const useRestoreAppBitriseYml = ({ appSlug }: AppLevelProps) => {
  const queryClient = useQueryClient();

  const url = restoreAppBitriseYMLPath(appSlug);
  return useMutation({
    mutationKey: [url],
    mutationFn: (yml: string) => monolithApi.post(url, { app_config_datastore_yaml: yml }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [appBitriseYMLPath(appSlug)] }),
  });
};

// Pipeline-level yml hooks
type PipelineLevelProps = {
  appSlug: string;
  pipelineId: string;
  attemptId: string;
  enabled?: boolean;
};
export const useHasPipelineBitriseYml = ({ appSlug, pipelineId, attemptId, enabled = true }: PipelineLevelProps) => {
  const url = hasPipelineBitriseYmlPath(appSlug, pipelineId, attemptId);
  return useQuery({
    queryKey: [url],
    queryFn: () => monolithApi.get<{ hasBitriseYml: boolean }>(url),
    select: ({ data }) => data.hasBitriseYml,
    enabled: !!appSlug && !!pipelineId && !!attemptId && enabled,
  });
};

export const usePipelineBitriseYml = ({ appSlug, pipelineId, attemptId, enabled }: PipelineLevelProps) => {
  const url = pipelineBitriseYMLPath(appSlug, pipelineId, attemptId);
  return useQuery({
    queryKey: [url],
    queryFn: () => monolithApi.get<{ bitriseYml: string }>(url),
    select: ({ data }) => data.bitriseYml,
    enabled: !!appSlug && !!pipelineId && !!attemptId && enabled,
  });
};

export const useDeletePipelineBitriseYml = ({ appSlug, pipelineId, attemptId }: PipelineLevelProps) => {
  const queryClient = useQueryClient();
  const url = deletePipelineBitriseYMLPath(appSlug, pipelineId);

  return useMutation({
    mutationKey: [url],
    mutationFn: () => monolithApi.delete(url),
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [hasPipelineBitriseYmlPath(appSlug, pipelineId, attemptId)],
        })
        .catch(() => {});
      queryClient
        .invalidateQueries({
          queryKey: [pipelineBitriseYMLPath(appSlug, pipelineId, attemptId)],
        })
        .catch(() => {});
    },
  });
};

// Build-level yml hooks
type BuildLevelProps = {
  buildSlug: string;
  enabled?: boolean;
};
export const useHasBuildBitriseYml = ({ buildSlug, enabled = true }: BuildLevelProps) => {
  const url = hasBuildBitriseYmlPath(buildSlug);
  return useQuery({
    queryKey: [url],
    queryFn: () => monolithApi.get<{ hasBitriseYml: boolean }>(url),
    select: ({ data }) => data.hasBitriseYml,
    enabled: !!buildSlug && enabled,
  });
};

export const useBuildBitriseYml = ({ buildSlug, enabled }: BuildLevelProps) => {
  const url = buildBitriseYMLPath(buildSlug);
  return useQuery({
    queryKey: [url],
    queryFn: () => monolithApi.get<{ bitriseYml: string }>(url),
    select: ({ data }) => data.bitriseYml,
    enabled: !!buildSlug && enabled,
  });
};

export const useDeleteBuildBitriseYml = ({ buildSlug }: BuildLevelProps) => {
  const queryClient = useQueryClient();
  const url = deleteBuildBitriseYMLPath(buildSlug);
  return useMutation({
    mutationKey: [url],
    mutationFn: () => monolithApi.delete(url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [hasBuildBitriseYmlPath(buildSlug)] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: [buildBitriseYMLPath(buildSlug)] }).catch(() => {});
    },
  });
};
